import React, { useState } from 'react';
import './Transfer.css';
import { API_URL } from '../config';
import TransferReview from './TransferReview';

const GENERIC_NOT_FOUND = "We couldn't find a match. Please double-check and try again.";

// Rendered only after the access gate passes. Name/Student ID and DOB are
// sent to the backend together in one call anyway, so they live on one
// combined identity step rather than two separate pages -- submit enables
// once (name OR student ID) AND DOB are filled. Then a phone-confirmation
// step (needs the server's masked hint first, so it can't be combined the
// same way), then hands off to TransferReview. Every failure at every step
// shows the exact same generic message -- never a hint about which field
// was wrong.
const TransferForm = ({ branchCode, code, branchName, introText, reviewText, matchFoundText, successText, alreadySubmittedText, grades }) => {
  const [step, setStep] = useState('identity'); // identity -> phone -> review
  const [name, setName] = useState('');
  const [studentId, setStudentId] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [phoneLastFour, setPhoneLastFour] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [matchResult, setMatchResult] = useState(null); // { primary_student, siblings }

  const canSubmitIdentity = (name.trim() || studentId.trim()) && dateOfBirth;

  const handleIdentitySubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitIdentity) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/transfer/lookup-hint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          branch: branchCode, code, name: name.trim(), student_id: studentId.trim(), date_of_birth: dateOfBirth,
        }),
      });
      const data = await res.json();
      if (data.found) {
        setPhoneHint(data.phone_hint || '');
        setStep('phone');
      } else {
        setError(data.error || GENERIC_NOT_FOUND);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (phoneLastFour.length !== 4) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/transfer/lookup-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          branch: branchCode, code, name: name.trim(), student_id: studentId.trim(),
          date_of_birth: dateOfBirth, phone_last_four: phoneLastFour,
        }),
      });
      const data = await res.json();
      if (data.found) {
        setMatchResult(data);
        setStep('review');
      } else {
        setError(data.error || GENERIC_NOT_FOUND);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'review' && matchResult) {
    return (
      <TransferReview
        branchCode={branchCode}
        code={code}
        branchName={branchName}
        reviewText={reviewText}
        matchFoundText={matchFoundText}
        successText={successText}
        alreadySubmittedText={alreadySubmittedText}
        grades={grades}
        name={name.trim()}
        studentId={studentId.trim()}
        dateOfBirth={dateOfBirth}
        phoneLastFour={phoneLastFour}
        primaryStudent={matchResult.primary_student}
        siblings={matchResult.siblings || []}
      />
    );
  }

  const hintDisplay = phoneHint ? `${phoneHint.replace(/(\d{3})(\d{3})/, '$1-$2-')}••••` : '••••';

  return (
    <div className="transfer-container">
      <h1 className="transfer-title">{branchName || 'Transfer Request'}</h1>
      <p className="transfer-subtitle">Let's find the student's record.</p>
      {step === 'identity' && introText && <div className="transfer-static-text">{introText}</div>}
      {error && <div className="transfer-error">{error}</div>}

      {step === 'identity' && (
        <form onSubmit={handleIdentitySubmit}>
          <div className="transfer-field">
            <label htmlFor="transfer-name">Student First or Last Name</label>
            <input id="transfer-name" type="text" value={name} onChange={e => setName(e.target.value)} autoFocus />
          </div>
          <div className="transfer-field">
            <label htmlFor="transfer-student-id">Student ID</label>
            <input
              id="transfer-student-id"
              type="text"
              placeholder="e.g. S1234"
              value={studentId}
              onChange={e => setStudentId(e.target.value)}
            />
          </div>
          <p className="transfer-hint">Enter the name or the Student ID — whichever you have.</p>
          <div className="transfer-field">
            <label htmlFor="transfer-dob">Date of Birth</label>
            <input id="transfer-dob" type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} />
          </div>
          <button type="submit" className="transfer-btn" disabled={submitting || !canSubmitIdentity}>
            {submitting ? 'Checking…' : 'Continue'}
          </button>
        </form>
      )}

      {step === 'phone' && (
        <form onSubmit={handlePhoneSubmit}>
          <div className="transfer-field">
            <label htmlFor="transfer-phone-last-four">
              Confirm the phone number on file: {hintDisplay}
            </label>
            <input
              id="transfer-phone-last-four"
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="Last 4 digits"
              value={phoneLastFour}
              onChange={e => setPhoneLastFour(e.target.value.replace(/\D/g, '').slice(0, 4))}
              autoFocus
            />
          </div>
          <p className="transfer-hint">Enter just the last 4 digits, no country code needed.</p>
          <div className="transfer-actions">
            <button
              type="button"
              className="transfer-btn transfer-btn-secondary"
              onClick={() => { setError(''); setPhoneLastFour(''); setStep('identity'); }}
            >
              Back
            </button>
            <button type="submit" className="transfer-btn" disabled={submitting || phoneLastFour.length !== 4}>
              {submitting ? 'Checking…' : 'Continue'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default TransferForm;
